import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion as Motion, useReducedMotion } from "motion/react";
import ChoiceGroup from "../ChoiceGroup/ChoiceGroup";
import FormField from "../FormField/FormField";
import HelpSupport from "../HelpSupport/HelpSupport";
import StepIndicator from "../StepIndicator/StepIndicator";
import { SCREEN_MOTION } from "../../config/screenMotion";
import { INITIAL_FORM, YES_NO_OPTIONS } from "../../data/formConfig";
import { TERMS_DOCUMENT_URL, TERMS_METADATA, TERMS_SECTIONS } from "../../data/terms";
import { getIbmValidationErrorMessage, getRegistrationByIbm, submitRegistration } from "../../services/registrationApi";
import { clearDraft, loadDraft, saveDraft } from "../../utils/storage";
import { formatCpf, formatPhone, validateStep } from "../../utils/validation";
import styles from "./RegistrationFlow.module.scss";

const STEP_CONTENT = [
  {
    title: "Preencha seus dados",
    subtitle: "Revise as informações com atenção antes de continuar.",
  },
  {
    title: "Informe sua viagem",
    subtitle: "Preencha os dados de chegada e hospedagem.",
  },
  {
    title: "Finalize seu cadastro",
    subtitle: "Leia os termos e finalize o seu cadastro.",
  },
];

const conditionalMotion = {
  initial: { opacity: 0, y: -20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 20 },
  transition: { duration: 0.3, ease: "easeInOut" },
};

const SHELL_PRIVACY_URL = "https://www.shell.com.br/politica-de-privacidade.html";

function removeCompanionData(data) {
  return {
    ...data,
    companion: "nao",
    companionName: "",
    companionSurname: "",
    companionBirthDate: "",
    companionCpf: "",
  };
}

function RegistrationFlow({ initialData, companionRestricted = false, onComplete, onLogin }) {
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    const initialForm = initialData
      ? { ...INITIAL_FORM, ...initialData }
      : loadDraft(INITIAL_FORM);
    const { accessibility: legacyAccessibility, ...currentForm } = initialForm;
    const accessibilityDetails = currentForm.accessibilityDetails
      || (Array.isArray(legacyAccessibility) ? legacyAccessibility.filter(Boolean).join(", ") : "");
    const normalizedForm = { ...currentForm, accessibilityDetails };
    return companionRestricted ? removeCompanionData(normalizedForm) : normalizedForm;
  });
  const [errors, setErrors] = useState({});
  const [validatedSteps, setValidatedSteps] = useState(() => new Set());
  const [submitError, setSubmitError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ibmValidation, setIbmValidation] = useState({ value: "", status: "idle", message: "" });

  const updateField = (event) => {
    const { name, type, checked, dataset } = event.target;
    if (companionRestricted && name === "companion") return;
    let { value } = event.target;
    if (name === "cpf" || name === "companionCpf") value = formatCpf(value);
    if (name === "phone") value = formatPhone(value);
    const nextValue = type === "checkbox" && dataset.choice !== "true" ? checked : value;

    const next = { ...form, [name]: nextValue };
    if (name === "hasSpecialNeeds" && value === "nao") next.specialNeedsDetails = "";
    if (name === "hasHealthPlan" && value === "nao") next.healthPlanName = "";
    if (name === "companion" && value === "nao") {
      next.companionName = "";
      next.companionSurname = "";
      next.companionBirthDate = "";
      next.companionCpf = "";
    }
    if (name === "dietaryRestrictions" && value === "nao") next.dietaryDetails = "";
    if (name === "ibm") setIbmValidation({ value: "", status: "idle", message: "" });
    setForm(next);
    saveDraft(next);
    if (validatedSteps.has(step)) setErrors(validateStep(step, next));
  };

  const markValidated = (stepNumber) => {
    setValidatedSteps((current) => new Set(current).add(stepNumber));
  };

  const focusFirstError = () => {
    requestAnimationFrame(() => document.querySelector('[aria-invalid="true"]')?.focus());
  };

  const validateIbm = async (ibm) => {
    const normalizedIbm = String(ibm ?? "").replace(/\D/g, "");

    if (ibmValidation.status === "valid" && ibmValidation.value === normalizedIbm) {
      return { valid: true };
    }

    setIbmValidation({ value: normalizedIbm, status: "loading", message: "Validando IBM..." });

    try {
      const result = await getRegistrationByIbm(normalizedIbm);
      if (!result?.ok || !result?.exists) {
        const message = result?.message || "IBM não encontrado na lista de liberados.";
        setIbmValidation({ value: normalizedIbm, status: "invalid", message });
        return { valid: false, message };
      }

      const message = result.message || "IBM liberado para inscrição.";
      setIbmValidation({ value: normalizedIbm, status: "valid", message });
      return { valid: true };
    } catch (error) {
      const message = getIbmValidationErrorMessage(error);
      setIbmValidation({ value: normalizedIbm, status: "invalid", message });
      return { valid: false, message };
    }
  };

  const goNext = async () => {
    let nextErrors = validateStep(step, form);
    markValidated(step);

    if (step === 1 && !nextErrors.ibm) {
      const ibmResult = await validateIbm(form.ibm);
      if (!ibmResult.valid) {
        nextErrors = { ...nextErrors, ibm: ibmResult.message };
      }
    }

    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      focusFirstError();
      return;
    }
    setStep((current) => Math.min(3, current + 1));
  };

  const goBack = () => {
    setErrors({});
    setStep((current) => Math.max(1, current - 1));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (step < 3) {
      await goNext();
      return;
    }

    const nextErrors = validateStep(3, form);
    markValidated(3);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length) {
      focusFirstError();
      return;
    }

    setIsSubmitting(true);
    setSubmitError("");
    try {
      await submitRegistration(form);
      clearDraft();
      onComplete();
      requestAnimationFrame(() => document.getElementById("success-title")?.focus());
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : "Ocorreu um erro inesperado.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const statusFor = (name, value) => {
    if (!validatedSteps.has(step)) return undefined;
    if (errors[name]) return "invalid";
    return String(value ?? "").trim() ? "valid" : undefined;
  };

  const currentContent = STEP_CONTENT[step - 1];

  return (
    <section className={styles.section} aria-live="polite">
      <AnimatePresence mode="wait" initial={false}>
        <Motion.div
          key={`registration-step-${step}`}
          className={styles.layout}
          {...SCREEN_MOTION}
        >
          <div className={styles.heading}>
            <div>
              <h1 id="flow-title">{currentContent.title}</h1>
              <p>{currentContent.subtitle}</p>
            </div>
            <StepIndicator currentStep={step} />
          </div>

          <form className={styles.form} onSubmit={handleSubmit} noValidate>
            {step === 1 ? (
              <PersonalData form={form} errors={errors} onChange={updateField} statusFor={statusFor} ibmValidation={ibmValidation} companionRestricted={companionRestricted} />
            ) : null}
            {step === 2 ? (
              <TravelData
                form={form}
                errors={errors}
                onChange={updateField}
                statusFor={statusFor}
              />
            ) : null}
            {step === 3 ? (
              <Terms form={form} errors={errors} onChange={updateField} />
            ) : null}

            {submitError ? <div className={styles.submitError} role="alert">{submitError}</div> : null}

            <div className={styles.actions}>
              <button className={styles.secondaryButton} type="button" onClick={step > 1 ? goBack : onLogin}>Voltar</button>
              <button className={styles.primaryButton} type="submit" disabled={isSubmitting || ibmValidation.status === "loading"}>
                {ibmValidation.status === "loading" ? "Validando IBM..." : isSubmitting ? "Enviando..." : step === 3 ? "Salvar e finalizar" : "Continuar"}
              </button>
            </div>
          </form>
          <HelpSupport />
        </Motion.div>
      </AnimatePresence>
    </section>
  );
}

function PersonalData({ form, errors, onChange, statusFor, ibmValidation, companionRestricted }) {
  return (
    <div className={styles.fields}>
      <section className={styles.formSection} aria-labelledby="personal-data-title">
        <h2 id="personal-data-title" className={styles.sectionTitle}>Dados cadastrais</h2>
        <div className={styles.twoColumns}>
          <FormField label="Nome" name="name" value={form.name} onChange={onChange} error={errors.name} status={statusFor("name", form.name)} autoComplete="given-name" hint="Digite apenas o primeiro nome." />
          <FormField label="Sobrenome" name="surname" value={form.surname} onChange={onChange} error={errors.surname} status={statusFor("surname", form.surname)} autoComplete="family-name" hint="Digite o sobrenome completo." />
        </div>
        <div className={styles.twoColumns}>
          <FormField label="IBM Posto" name="ibm" value={form.ibm} onChange={onChange} error={errors.ibm} status={ibmValidation.status === "valid" ? "valid" : statusFor("ibm", form.ibm)} inputMode="numeric" maxLength={12} hint={ibmValidation.message || "Utilize apenas números."} />
          <FormField label="CPF" name="cpf" value={form.cpf} onChange={onChange} error={errors.cpf} status={statusFor("cpf", form.cpf)} inputMode="numeric" autoComplete="off" maxLength={14} hint="Utilize apenas números." />
        </div>
        <FormField label="Data de nascimento" name="birthDate" type="date" value={form.birthDate} onChange={onChange} error={errors.birthDate} status={statusFor("birthDate", form.birthDate)} autoComplete="bday" max={new Date().toISOString().slice(0, 10)} hint="Digite sua data de nascimento." />
        <div className={styles.twoColumns}>
          <FormField label="E-mail" name="email" type="email" value={form.email} onChange={onChange} error={errors.email} status={statusFor("email", form.email)} autoComplete="email" hint="Digite o seu e-mail." />
          <FormField label="Celular" name="phone" type="tel" value={form.phone} onChange={onChange} error={errors.phone} status={statusFor("phone", form.phone)} autoComplete="tel" maxLength={15} hint="Informe o DDD e o número." />
        </div>
      </section>

      <section className={styles.formSection} aria-label="Informações complementares">
        <ChoiceGroup legend="Possui alguma necessidade especial?" name="hasSpecialNeeds" value={form.hasSpecialNeeds} options={YES_NO_OPTIONS} onChange={onChange} error={errors.hasSpecialNeeds} status={statusFor("hasSpecialNeeds", form.hasSpecialNeeds)} variant="plain" />
        <AnimatePresence initial={false}>
          {form.hasSpecialNeeds === "sim" ? <Motion.div key="special-needs" {...conditionalMotion}><FormField label="Necessidade" name="specialNeedsDetails" value={form.specialNeedsDetails} onChange={onChange} error={errors.specialNeedsDetails} status={statusFor("specialNeedsDetails", form.specialNeedsDetails)} hint="Digite qual é a sua necessidade especial." /></Motion.div> : null}
        </AnimatePresence>
        <ChoiceGroup legend="Possui plano de saúde?" name="hasHealthPlan" value={form.hasHealthPlan} options={YES_NO_OPTIONS} onChange={onChange} error={errors.hasHealthPlan} status={statusFor("hasHealthPlan", form.hasHealthPlan)} variant="plain" />
        <AnimatePresence initial={false}>
          {form.hasHealthPlan === "sim" ? <Motion.div key="health-plan" {...conditionalMotion}><FormField label="Nome do plano" name="healthPlanName" value={form.healthPlanName} onChange={onChange} error={errors.healthPlanName} status={statusFor("healthPlanName", form.healthPlanName)} hint="Digite o nome do seu plano de saúde." /></Motion.div> : null}
        </AnimatePresence>
        <ChoiceGroup
          legend="Vai levar acompanhante?"
          name="companion"
          value={form.companion}
          options={YES_NO_OPTIONS}
          onChange={onChange}
          error={errors.companion}
          status={statusFor("companion", form.companion)}
          variant="plain"
          disabled={companionRestricted}
          hint={companionRestricted ? "Acompanhante não disponível para este cadastro." : undefined}
        />
        <AnimatePresence initial={false}>
          {form.companion === "sim" ? (
            <Motion.div key="companion-fields" className={styles.companionFields} {...conditionalMotion}>
              <h3 className={styles.companionTitle}>Dados do acompanhante</h3>
              <div className={styles.twoColumns}>
                <FormField label="Nome" name="companionName" value={form.companionName} onChange={onChange} error={errors.companionName} status={statusFor("companionName", form.companionName)} hint="Digite apenas o primeiro nome." />
                <FormField label="Sobrenome" name="companionSurname" value={form.companionSurname} onChange={onChange} error={errors.companionSurname} status={statusFor("companionSurname", form.companionSurname)} hint="Digite o sobrenome completo." />
              </div>
              <div className={styles.twoColumns}>
                <FormField label="Data de nascimento" name="companionBirthDate" type="date" value={form.companionBirthDate} onChange={onChange} error={errors.companionBirthDate} status={statusFor("companionBirthDate", form.companionBirthDate)} hint="Digite a data de nascimento." />
                <FormField label="CPF" name="companionCpf" value={form.companionCpf} onChange={onChange} error={errors.companionCpf} status={statusFor("companionCpf", form.companionCpf)} inputMode="numeric" maxLength={14} hint="Utilize apenas números." />
              </div>
            </Motion.div>
          ) : null}
        </AnimatePresence>
      </section>
    </div>
  );
}

function TravelData({ form, errors, onChange, statusFor }) {
  return (
    <div className={styles.fields}>
      <section className={styles.formSection} aria-labelledby="flight-data-title">
        <h2 id="flight-data-title" className={styles.sectionTitle}>Dados de voo</h2>
        <div className={styles.twoColumns}>
          <FormField label="Cidade de origem" name="originCity" value={form.originCity} onChange={onChange} error={errors.originCity} status={statusFor("originCity", form.originCity)} autoComplete="address-level2" hint="Informe a cidade de onde você partirá." />
          <FormField label="Horário previsto de chegada" name="arrivalTime" type="time" value={form.arrivalTime} onChange={onChange} status={statusFor("arrivalTime", form.arrivalTime)} requiredField={false} hint="Preencha caso já tenha essa informação." />
        </div>
      </section>

      <section className={styles.formSection} aria-labelledby="lodging-title">
        <h2 id="lodging-title" className={styles.sectionTitle}>Hospedagem</h2>
        <aside className={styles.infoBox}><p>As informações do hotel serão confirmadas pela organização do evento.</p></aside>
        <ChoiceGroup legend="Preferência de acomodação" name="bedPreference" value={form.bedPreference} options={[{ value: "casal", label: "Cama de casal" }, { value: "duas", label: "Duas camas" }]} onChange={onChange} error={errors.bedPreference} status={statusFor("bedPreference", form.bedPreference)} />
      </section>

      <section className={styles.formSection} aria-labelledby="preferences-title">
        <h2 id="preferences-title" className={styles.sectionTitle}>Preferências e necessidades</h2>
        <ChoiceGroup legend="Possui restrições alimentares?" name="dietaryRestrictions" value={form.dietaryRestrictions} options={YES_NO_OPTIONS} onChange={onChange} error={errors.dietaryRestrictions} status={statusFor("dietaryRestrictions", form.dietaryRestrictions)} variant="plain" />
        <AnimatePresence initial={false}>
          {form.dietaryRestrictions === "sim" ? <Motion.div key="dietary" {...conditionalMotion}><FormField label="Quais restrições?" name="dietaryDetails" value={form.dietaryDetails} onChange={onChange} error={errors.dietaryDetails} status={statusFor("dietaryDetails", form.dietaryDetails)} hint="Descreva suas restrições alimentares." /></Motion.div> : null}
        </AnimatePresence>
        <ChoiceGroup legend="Tem necessidades especiais?" name="hasAccessibilityNeeds" value={form.hasAccessibilityNeeds} options={YES_NO_OPTIONS} onChange={onChange} error={errors.hasAccessibilityNeeds} status={statusFor("hasAccessibilityNeeds", form.hasAccessibilityNeeds)} variant="plain" />
        <AnimatePresence initial={false}>
          {form.hasAccessibilityNeeds === "sim" ? (
            <Motion.div key="accessibility-needs" {...conditionalMotion}>
              <FormField label="Descreva sua necessidade" name="accessibilityDetails" value={form.accessibilityDetails} onChange={onChange} error={errors.accessibilityDetails} status={statusFor("accessibilityDetails", form.accessibilityDetails)} hint="Informe qual é a sua necessidade especial." />
            </Motion.div>
          ) : null}
        </AnimatePresence>
      </section>
    </div>
  );
}

function Terms({ form, errors, onChange }) {
  const [isTermsOpen, setIsTermsOpen] = useState(false);

  return (
    <>
      <div className={styles.fields}>
        <div className={`${styles.checkLine} ${errors.acceptPrivacy ? styles.invalidChoice : ""}`}>
          <input id="accept-privacy" type="checkbox" name="acceptPrivacy" checked={form.acceptPrivacy} onChange={onChange} aria-invalid={Boolean(errors.acceptPrivacy)} aria-describedby={errors.acceptPrivacy ? "accept-privacy-error" : undefined} />
          <label htmlFor="accept-privacy">
            <strong>LGPD:</strong> Declaro que li e concordo com os <button className={styles.termTrigger} type="button" onClick={(event) => { event.preventDefault(); setIsTermsOpen(true); }}>Termos e Condições de Uso</button> e a <a href={SHELL_PRIVACY_URL} target="_blank" rel="noreferrer">Política de Privacidade da Shell</a>.
          </label>
        </div>
        {errors.acceptPrivacy ? <span id="accept-privacy-error" className={styles.fieldError} role="alert">{errors.acceptPrivacy}</span> : null}
      </div>

      <AnimatePresence>
        {isTermsOpen ? <TermsModal onClose={() => setIsTermsOpen(false)} /> : null}
      </AnimatePresence>
    </>
  );
}

function TermsModal({ onClose }) {
  const dialogRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const transition = { duration: reduceMotion ? 0 : 0.22, ease: "easeOut" };

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    dialogRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === "Escape") onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
      previouslyFocused?.focus?.();
    };
  }, [onClose]);

  return (
    <Motion.div
      className={styles.modalOverlay}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={transition}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <Motion.section
        ref={dialogRef}
        className={styles.termsModal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="terms-modal-title"
        tabIndex={-1}
        initial={{ opacity: 0, y: reduceMotion ? 0 : 60 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: reduceMotion ? 0 : 60 }}
        transition={transition}
      >
        <header className={styles.modalHeader}>
          <h2 id="terms-modal-title">Termos e Condições de Uso</h2>
          <button type="button" onClick={onClose} aria-label="Fechar termos e condições">×</button>
        </header>
        <div className={`${styles.modalContent} ${styles.termsContent}`}>
          <TermsDocumentContent />
        </div>
        <footer className={styles.modalFooter}>
          <a className={styles.documentDownload} href={TERMS_DOCUMENT_URL} download>Baixar documento</a>
          <button type="button" onClick={onClose}>Entendi</button>
        </footer>
      </Motion.section>
    </Motion.div>
  );
}

function TermsDocumentContent() {
  return (
    <article className={styles.termsDocument}>
      <header className={styles.termsDocumentHeader}>
        <strong>{TERMS_METADATA.title}</strong>
        <span>{TERMS_METADATA.subtitle}</span>
        <span>{TERMS_METADATA.cycle}</span>
        <small>{TERMS_METADATA.updated}</small>
      </header>

      {TERMS_SECTIONS.map((section) => (
        <section className={styles.termsSection} key={section.number}>
          <h3>{section.number}. {section.title}</h3>
          {section.items.map((item) => (
            <div className={styles.termsParagraph} key={item.number}>
              <span className={styles.termsMarker}>{item.number}.</span>
              <div>
                <p>{item.text}</p>
                {item.children.length ? (
                  <ul className={styles.termsChildren}>
                    {item.children.map((child, index) => (
                      <li key={`${item.number}-${child.marker || "detail"}-${index}`}>
                        <span>{child.marker}</span>
                        <p>{child.text}</p>
                      </li>
                    ))}
                  </ul>
                ) : null}
              </div>
            </div>
          ))}
        </section>
      ))}
    </article>
  );
}

export default RegistrationFlow;


